import { Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { CaseAnalyticsStatusDistributionCurrentUser } from 'dashboard/models/case-analytics';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { DEFAULT_COLUMN_CONFIGURATION } from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable';
import DashboardHomeCasesTable from 'dashboard/views/DashboardHomeView/DashboardHomeCasesTable';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import useProductFeature from 'lib/hooks/useProductFeature';

export interface DashboardHomeCasesActivityProps {
  cases: Case[];
  caseStatusDistributionForCurrentUser: CaseAnalyticsStatusDistributionCurrentUser;
  caseCaseQueues: CaseQueue[];
  caseUsers: User[];
  caseTags: Tag[];
  caseAccounts: Account[];
  caseStates: { states: CaseTemplateBasicsOption['states']; initialStateToken: string }[];
  isCasesLoading: boolean;
  isCaseAnalyticsLoading: boolean;
  isCaseStatesLoading: boolean;
  isCaseTagsLoading: boolean;
}

const DashboardHomeCasesActivity = ({
  cases,
  caseStatusDistributionForCurrentUser,
  caseCaseQueues,
  caseUsers,
  caseTags,
  caseAccounts,
  caseStates,
  isCasesLoading,
  isCaseAnalyticsLoading,
  isCaseStatesLoading,
  isCaseTagsLoading,
}: DashboardHomeCasesActivityProps) => {
  const hasCasesEnhanced = useProductFeature(ProductFeatureName.CasesEnhanced);
  const hasCasesEnterprise = useProductFeature(ProductFeatureName.CasesEnterprise);

  const hiddenColumns: Record<string, boolean> = {
    active_sla_expires_at: !hasCasesEnhanced && !hasCasesEnterprise,
  };
  const columnOrdering = DEFAULT_COLUMN_CONFIGURATION.filter(
    (column) => !hiddenColumns[column.key],
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const casesHomepageAnalytics: {
    openCases: { countAssignedToCurrentUser: number; states: CaseTemplateBasicsOption['states'] };
    inProgressCases: {
      countAssignedToCurrentUser: number;
      states: CaseTemplateBasicsOption['states'];
    };
    resolvedCases: {
      countAssignedToCurrentUser: number;
      states: CaseTemplateBasicsOption['states'];
    };
    countResolvedTodayByCurrentUser: number;
    countSlaExpired: number;
    countSlaExpiresNextHour: number;
  } = useMemo(() => {
    const result = {
      openCases: {
        countAssignedToCurrentUser: 0,
        states: [] as CaseTemplateBasicsOption['states'],
      },
      inProgressCases: {
        countAssignedToCurrentUser: 0,
        states: [] as CaseTemplateBasicsOption['states'],
      },
      resolvedCases: {
        countAssignedToCurrentUser: 0,
        states: [] as CaseTemplateBasicsOption['states'],
      },
      countResolvedTodayByCurrentUser: 0,
      countSlaExpired: 0,
      countSlaExpiresNextHour: 0,
    };

    if (isCaseAnalyticsLoading || isCaseStatesLoading) {
      return;
    }

    // Categorize the case states into open vs. in progress states
    caseStates.forEach((caseStatesPerTemplate) => {
      const initialCaseState = caseStatesPerTemplate.states.find(
        (s) => s.token === caseStatesPerTemplate.initialStateToken,
      );
      const inProgressCaseStates = caseStatesPerTemplate.states.filter(
        (s) =>
          !s.archived && !(s.token === caseStatesPerTemplate.initialStateToken) && !s['end-state'],
      );
      const endStates = caseStatesPerTemplate.states.filter((s) => s['end-state']);
      // The first state in validCaseStates is always the initial state for this Case Template
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      result.openCases.states = [...result.openCases.states, initialCaseState];
      result.inProgressCases.states = [...result.inProgressCases.states, ...inProgressCaseStates];
      result.resolvedCases.states = [...result.resolvedCases.states, ...endStates];
    });

    // Calculate the counts assigned to current user
    const initialCaseStateIds = result.openCases.states.map((s) => s.token);
    const inProgressCaseStateIds = result.inProgressCases.states.map((s) => s.token);
    const resolvedCaseStateIds = result.resolvedCases.states.map((s) => s.token);

    const statusDistribution = caseStatusDistributionForCurrentUser.data.count_by_state;

    Object.keys(statusDistribution).forEach((stateId) => {
      if (initialCaseStateIds.includes(stateId)) {
        result.openCases.countAssignedToCurrentUser += statusDistribution[stateId];
      } else if (inProgressCaseStateIds.includes(stateId)) {
        result.inProgressCases.countAssignedToCurrentUser += statusDistribution[stateId];
      } else if (resolvedCaseStateIds.includes(stateId)) {
        result.resolvedCases.countAssignedToCurrentUser += statusDistribution[stateId];
      }
    });
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result.countResolvedTodayByCurrentUser =
      caseStatusDistributionForCurrentUser.data.count_resolved_today;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result.countSlaExpired = caseStatusDistributionForCurrentUser.data.count_sla_expired;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result.countSlaExpiresNextHour =
      caseStatusDistributionForCurrentUser.data.count_sla_expires_next_hour;

    return result;
  }, [
    isCaseAnalyticsLoading,
    isCaseStatesLoading,
    caseStatusDistributionForCurrentUser,
    caseStates,
  ]);

  return (
    <>
      {isCasesLoading || isCaseAnalyticsLoading || isCaseStatesLoading || isCaseTagsLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <MetricsCards>
            <MetricsCard>
              <Paragraph>Cases you resolved today</Paragraph>
              <MetricsCardRow>
                <Heading size='md' color='primary' as='p'>
                  {casesHomepageAnalytics.countResolvedTodayByCurrentUser}
                </Heading>
                <HelpIcon>
                  Includes Cases you resolved today that are in these statuses:
                  <br />
                  <UnorderedList>
                    {casesHomepageAnalytics.resolvedCases.states
                      .map((s) => s.name)
                      .filter((name, index, self) => self.indexOf(name) === index)
                      .map((name) => (
                        <li key={`home-cases-analytics__state-${name}`}>{name}</li>
                      ))}
                  </UnorderedList>
                </HelpIcon>
              </MetricsCardRow>
            </MetricsCard>
            <MetricsCard>
              <Paragraph>Open Cases assigned to you</Paragraph>
              <MetricsCardRow>
                <Heading size='md' color='primary' as='p'>
                  {casesHomepageAnalytics.openCases.countAssignedToCurrentUser}
                </Heading>
                <HelpIcon>
                  Includes Cases created in the past month that are in these statuses:
                  <br />
                  <UnorderedList>
                    {casesHomepageAnalytics.openCases.states
                      .map((s) => s.name)
                      .filter((name, index, self) => self.indexOf(name) === index)
                      .map((name) => (
                        <li key={`home-cases-analytics__state-${name}`}>{name}</li>
                      ))}
                  </UnorderedList>
                </HelpIcon>
              </MetricsCardRow>
            </MetricsCard>
            <MetricsCard>
              <Paragraph>In Progress Cases assigned to you</Paragraph>
              <MetricsCardRow>
                <Heading size='md' color='primary' as='p'>
                  {casesHomepageAnalytics.inProgressCases.countAssignedToCurrentUser}
                </Heading>
                <HelpIcon>
                  Includes Cases created in the past month that are in these statuses:
                  <br />
                  <UnorderedList>
                    {casesHomepageAnalytics.inProgressCases.states
                      .map((s) => s.name)
                      .filter((name, index, self) => self.indexOf(name) === index)
                      .map((name) => (
                        <li key={`home-cases-analytics__state-${name}`}>{name}</li>
                      ))}
                  </UnorderedList>
                </HelpIcon>
              </MetricsCardRow>
            </MetricsCard>
            {(hasCasesEnhanced || hasCasesEnterprise) && (
              <MetricsCard>
                <Paragraph>Cases overdue</Paragraph>
                <MetricsCardRow>
                  <Heading size='md' color='primary' as='p'>
                    {casesHomepageAnalytics.countSlaExpired}
                  </Heading>
                  <HelpIcon>
                    Includes Cases created in the past month overdue that are assigned to you and in
                    these statuses:
                    <br />
                    <UnorderedList>
                      {[
                        ...casesHomepageAnalytics.openCases.states,
                        ...casesHomepageAnalytics.inProgressCases.states,
                      ]
                        .map((s) => s.name)
                        .filter((name, index, self) => self.indexOf(name) === index)
                        .map((name) => (
                          <li key={`home-cases-analytics__state-${name}`}>{name}</li>
                        ))}
                    </UnorderedList>
                  </HelpIcon>
                </MetricsCardRow>
              </MetricsCard>
            )}
            {(hasCasesEnhanced || hasCasesEnterprise) && (
              <MetricsCard>
                <Paragraph>Cases due in the next hour</Paragraph>
                <MetricsCardRow>
                  <Heading size='md' color='primary' as='p'>
                    {casesHomepageAnalytics.countSlaExpiresNextHour}
                  </Heading>
                  <HelpIcon>
                    Includes Cases created in the past month due in the next hour that are assigned
                    to you and in these statuses:
                    <br />
                    <UnorderedList>
                      {[
                        ...casesHomepageAnalytics.openCases.states,
                        ...casesHomepageAnalytics.inProgressCases.states,
                      ]
                        .map((s) => s.name)
                        .filter((name, index, self) => self.indexOf(name) === index)
                        .map((name) => (
                          <li key={`home-cases-analytics__state-${name}`}>{name}</li>
                        ))}
                    </UnorderedList>
                  </HelpIcon>
                </MetricsCardRow>
              </MetricsCard>
            )}
          </MetricsCards>
          <Spacer height={1} />
          <StyledTableWrapper>
            <DashboardHomeCasesTable
              accounts={caseAccounts}
              cases={cases}
              caseCaseQueues={caseCaseQueues}
              caseUsers={caseUsers}
              tags={caseTags}
              columnOrdering={columnOrdering}
            />
          </StyledTableWrapper>
        </>
      )}
    </>
  );
};

const StyledTableWrapper = styled('div')`
  overflow: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const MetricsCards = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[4]};
`;

const MetricsCard = styled('div')`
  display: grid;
  padding: ${Spacing[4]};
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  width: 200px;
`;

const MetricsCardRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: end;
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
`;

export default DashboardHomeCasesActivity;
